import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { PlanService } from '../services/planService';
import { IInstitutionPlan, PlanTier } from '../interfaces/institution.interface';

export interface PlanAccess {
    planTier: PlanTier;
    employeeCount: number;
    employeeCap: number;
    allowedModules: string[];
    /** False while the plan fetch for the current institution is still in flight. */
    ready: boolean;
    /**
     * Re-runs the plan fetch for the current institution. Callers that mutate seat count
     * (create/remove a member) call this after a successful mutation so the seat meter doesn't
     * stay stale until the next remount/institution switch.
     */
    refresh: () => void;
}

// A plan fetch failure must never lock the UI, so an error (or no institution selected yet)
// fails open to PAID-like behavior: no cap surfaced, no seat meter, no nudge.
const FAIL_OPEN_PLAN: Omit<PlanAccess, 'ready' | 'refresh'> = {
    planTier: 'PAID',
    employeeCount: 0,
    employeeCap: 0,
    allowedModules: [],
};

const NOOP_REFRESH = () => {};

/**
 * @param inert when true a provider above is already supplying the answer, so this copy skips
 *              the network call. The hook still runs (rules of hooks) but its result is discarded.
 */
function useComputedPlan(inert = false): PlanAccess {
    const { selectedInstitutionId } = useAuth();
    const [plan, setPlan] = useState<Omit<PlanAccess, 'ready' | 'refresh'>>(FAIL_OPEN_PLAN);
    const [ready, setReady] = useState(false);
    // Bumped by refresh() to re-run the fetch effect below without changing selectedInstitutionId.
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (inert) return;
        if (!selectedInstitutionId) {
            setPlan(FAIL_OPEN_PLAN);
            setReady(true);
            return;
        }
        let alive = true;
        setReady(false);
        PlanService.getPlan(selectedInstitutionId)
            .then((p: IInstitutionPlan) => { if (alive) setPlan(p); })
            .catch(() => { if (alive) setPlan(FAIL_OPEN_PLAN); })
            .finally(() => { if (alive) setReady(true); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId, inert, version]);

    const refresh = useCallback(() => {
        if (inert) return;
        setVersion(v => v + 1);
    }, [inert]);

    return { ...plan, ready, refresh: inert ? NOOP_REFRESH : refresh };
}

const PlanContext = createContext<PlanAccess | null>(null);

/**
 * Computes the institution's plan tier + seat usage ONCE for everything below it.
 *
 * Without it, every consumer (seat meter, upgrade nudges, future gates) would issue its own
 * `getPlan` call and refetch on every remount. Mounted at the authenticated shell in App.tsx,
 * matching DashboardAccessProvider's shape, the fetch runs once per institution selection.
 *
 * The hook deliberately still works WITHOUT this provider: `usePlan` falls back to computing
 * locally, so a component (or a test) rendered on its own behaves exactly as before.
 */
export function PlanProvider({ children }: { children: ReactNode }) {
    const value = useComputedPlan();
    return createElement(PlanContext.Provider, { value }, children);
}

export function usePlan(): PlanAccess {
    const shared = useContext(PlanContext);
    // Hooks must run unconditionally, so the local computation always runs; when a provider is
    // present its value wins and the local copy is inert (see useComputedPlan's `inert` param).
    const local = useComputedPlan(!!shared);
    return shared ?? local;
}
